import {
  Badge,
  Datatable,
  DatatableBody,
  DatatableCell,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
  Skeleton,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

import {
  SortableHead,
  TableSkeletonActionsCell,
  TableSkeletonRow,
  type SortDirection,
} from '@/shared/ui/data-table';
import { TABLE_COL_WIDTH } from '@/shared/constants';

import { ADMIN_TABLE_STYLES } from '../adminTableStyles';
import { AUTH_TYPE_LABEL_KEY } from './authTypes';
import { RowMenu } from './RowMenu';
import type { McpCatalogRow, McpCatalogSortKey } from './McpCatalogTable.types';

/** The kebab sits `md` in from the row's edge; the column is exactly the button plus that inset. */
const ACTIONS_CELL = `${TABLE_COL_WIDTH.actions} pl-0 pr-md`;

const SKELETON_ROWS = Array.from({ length: 5 }, (_, index) => index);

interface McpCatalogTableProps {
  rows: McpCatalogRow[];
  isLoading: boolean;
  sortBy: McpCatalogSortKey;
  sortDirection: SortDirection;
  onSort: (column: McpCatalogSortKey) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  deleteDisabled: boolean;
}

export function McpCatalogTable({
  rows,
  isLoading,
  sortBy,
  sortDirection,
  onSort,
  onEdit,
  onDelete,
  deleteDisabled,
}: McpCatalogTableProps) {
  const { t } = useTranslation('admin');

  return (
    <Datatable
      aria-label={t('admin.mcpCatalogPage.mcpCatalog')}
      aria-busy={isLoading || undefined}
      // Fixed layout lets the Name column take whatever the others leave and truncate inside it,
      // where content sizing would widen the table past its card on a long description.
      className={`table-fixed ${ADMIN_TABLE_STYLES}`}
    >
      <DatatableHeader>
        <DatatableRow>
          <SortableHead
            column="name"
            label={t('admin.mcpCatalogPanel.name')}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <DatatableHead scope="col" className="w-1/4">
            {t('admin.mcpCatalogPanel.url')}
          </DatatableHead>
          <SortableHead
            column="authType"
            label={t('admin.mcpCatalogPanel.auth')}
            className={TABLE_COL_WIDTH.statusBadge}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          {/* The kebab column carries no heading: the icon in each row says what it is. */}
          <DatatableHead scope="col" className={ACTIONS_CELL} />
        </DatatableRow>
      </DatatableHeader>

      <DatatableBody>
        {isLoading
          ? SKELETON_ROWS.map((index) => (
              <TableSkeletonRow key={index} testId="mcp-catalog-skeleton-row">
                <DatatableCell>
                  <Skeleton className="h-4 w-40" />
                </DatatableCell>
                <DatatableCell>
                  <Skeleton className="h-4 w-64" />
                </DatatableCell>
                <DatatableCell>
                  <Skeleton className="h-5 w-24 rounded-full" />
                </DatatableCell>
                <TableSkeletonActionsCell className="pl-0 pr-md" />
              </TableSkeletonRow>
            ))
          : rows.map((row) => (
              <DatatableRow
                key={row.id}
                interactive
                // Overrides the variant's own hover tint.
                className="h-[72px] hover:[&>*]:bg-surface"
                onClick={() => onEdit(row.id)}
              >
                <DatatableCell>
                  <div className="flex min-w-0 flex-col gap-3xs">
                    {/* A `<tr>` takes no focus, so the name is the keyboard's way into the row. */}
                    <button
                      type="button"
                      className="max-w-full self-start truncate rounded-sm text-left text-body-md font-medium text-foreground outline-none focus-visible:focus-ring"
                      onClick={(event) => {
                        // The row opens the sheet too; without this it would open twice.
                        event.stopPropagation();
                        onEdit(row.id);
                      }}
                    >
                      {row.name}
                    </button>
                    {row.description && (
                      <span className="block truncate text-body-sm text-muted-foreground">
                        {row.description}
                      </span>
                    )}
                  </div>
                </DatatableCell>
                {/* `type-body-md`, not `text-body-md`: the cell's class merge reads the latter as a
                    colour, so it would drop `text-foreground` and keep the primitive's `text-base`. */}
                <DatatableCell className="truncate type-body-md">{row.url}</DatatableCell>
                <DatatableCell>
                  <Badge variant="secondary" size="sm" roundness="full">
                    {t(AUTH_TYPE_LABEL_KEY[row.authType])}
                  </Badge>
                </DatatableCell>
                {/* The row opens the server on click, and React bubbles through the menu's portal,
                    so no click in here may reach it. */}
                <DatatableCell
                  className={ACTIONS_CELL}
                  onClick={(event) => event.stopPropagation()}
                >
                  <RowMenu
                    onEdit={() => onEdit(row.id)}
                    onDelete={() => onDelete(row.id)}
                    deleteDisabled={deleteDisabled}
                  />
                </DatatableCell>
              </DatatableRow>
            ))}
      </DatatableBody>
    </Datatable>
  );
}

McpCatalogTable.displayName = 'McpCatalogTable';
